/**
 * Mandant-Regelwerk-Doku — rendert das effektive Regelwerk des gewählten
 * Mandanten für Birgit zum Nachlesen. Nicht konfigurierbar (per Owner-Decision
 * 2026-05-18: "muss nicht konfigurierbar sein").
 *
 * T04 (2026-07-07): Quelle ist NICHT mehr eine Frontend-Kopie, sondern
 * GET /api/client-rules — der Hub antwortet aus derselben loadClientRuleset-
 * Funktion (Code-Default ⊕ client_rules-DB-Row), mit der der Worker rechnet.
 * Anzeige == Rechnung. Mandant ohne Regelwerk → expliziter fail-loud-Zustand
 * (kein stiller Sibit-Fallback mehr).
 */
import { useClientContext } from "../lib/ClientContext";
import { useClientRuleset } from "../lib/client-rules";
import { Link } from "react-router-dom";

export function SettingsClientRules() {
  const { selectedClientId } = useClientContext();
  const { ruleset, hasDbRow, error } = useClientRuleset(selectedClientId);

  if (ruleset === undefined) {
    return <div className="muted" style={{ padding: 16 }}>Regelwerk wird geladen…</div>;
  }

  if (ruleset === null) {
    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Mandant-Regelwerk</h3>
        <div style={{
          padding: 12, marginTop: 16, background: "#fef2f2",
          border: "1px solid #fca5a5", borderRadius: 6, fontSize: 13,
        }}>
          <strong>Kein Regelwerk für diesen Mandanten hinterlegt.</strong>
          <div style={{ marginTop: 4, color: "#7f1d1d" }}>
            {error
              ? `Fehler: ${error}`
              : "Die Berechnung würde für Karten dieses Mandanten bewusst stoppen (fail-loud) — "
                + "es gibt keinen stillen Rückfall auf das Standard-Regelwerk. Zum Aktivieren: "
                + "client_rules-Eintrag für diesen Mandanten anlegen (Entwickler-Aufgabe)."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Mandant-Regelwerk</h3>
      <p className="muted" style={{ fontSize: 13, maxWidth: 820 }}>
        Harte Regeln pro Mandant für Mehrstunden- und Überstunden-Klassifikation
        in Phase 4 — Berechnung. Quelle: <code>GET /api/client-rules</code>{" "}
        (= exakt die Sicht, mit der der Worker rechnet — Mirror-Paar{" "}
        <code>worker/src/client-rules.ts ⇄ hub/src/client-rules.ts</code>).
        Der <strong>Mandantenstandard ist nicht über die Oberfläche konfigurierbar</strong> — Änderungen laufen als
        client_rules-Eintrag bzw. Code-Update + Deploy. Individuelle Wochen-Überstundengrenzen
        können bei den Mitarbeitern hinterlegt werden; sie ersetzen nur diese Wochen-Schwelle.
      </p>

      <div style={{
        padding: 12, marginTop: 16, marginBottom: 16,
        background: hasDbRow ? "#ecfdf5" : "#eff6ff",
        border: `1px solid ${hasDbRow ? "#86efac" : "#93c5fd"}`,
        borderRadius: 6, fontSize: 13,
      }}>
        <strong>Aktives Regelwerk:</strong> {ruleset.client_name}
        <span style={{ marginLeft: 8, color: "#374151" }}>
          {hasDbRow
            ? "(eigener client_rules-Eintrag in der Datenbank)"
            : "(Code-Standard — kein DB-Override)"}
        </span>
        <div style={{ marginTop: 4, fontSize: 12, color: "#374151" }}>
          gültig ab {ruleset.effective_from}
          {ruleset.effective_until && ` bis ${ruleset.effective_until}`}
        </div>
      </div>

      {(ruleset as any).calc_model?.startsWith("zulagen") && (
        <div style={{
          padding: 10, marginBottom: 16, fontSize: 13, borderRadius: 6,
          background: "#eff6ff", border: "1px solid #93c5fd", color: "#1e3a5f",
        }}>
          Dieser Mandant rechnet <strong>keine Stunden-/Überstunden-Logik</strong> —
          Berechnungsmodell: Monatssummen je Mitarbeiter × Zulagenart × Quelle
          (PDF und Excel getrennt). Maßgeblich ist die Übersetzungsmatrix unten.
        </div>
      )}
      {!(ruleset as any).calc_model?.startsWith("zulagen") && (<>
      <h4 style={{ marginBottom: 8 }}>Eckdaten</h4>
      <table className="data-table" style={{ marginBottom: 24, fontSize: 13 }}>
        <tbody>
          <tr>
            <td><strong>Wochen-Schwelle Überstunden 50%</strong></td>
            <td>
              {ruleset.ueberstunden_weekly_threshold_h} h pro Woche (Standard)
              <div style={{ marginTop: 4, fontSize: 12 }}>
                Individuelle Mitarbeitergrenzen haben Vorrang. Ohne individuelle Einstellung gilt dieser Standard.
                {" "}<Link to="/settings/employees#weekly-threshold">Individuelle Grenzen bei Mitarbeitern verwalten</Link>
              </div>
            </td>
          </tr>
          <tr>
            <td><strong>Tages-Schwelle Überstunden 50%</strong></td>
            <td>{ruleset.daily_ueberstunden_threshold_h} h pro Tag (Kombination mit Woche: {ruleset.ueberstunden_combine === "max" ? "Maximum — keine Doppelzählung" : "Summe"})</td>
          </tr>
          <tr>
            <td><strong>Überstunden gegen Minusstunden verrechenbar</strong></td>
            <td>{ruleset.ueberstunden_offsettable_by_minus ? "Ja" : "Nein — unantastbar, werden immer bezuschlagt"}</td>
          </tr>
          <tr>
            <td><strong>ZA-Konto-Modus</strong></td>
            <td>{ruleset.za_konto_mode === "saldo" ? "Echter Monatssaldo (Minusstunden gegengerechnet)" : "Nur positive Mehrstunden (Gutstunden)"}</td>
          </tr>
          <tr>
            <td><strong>Abwesenheiten mit Sollzeit-Gutschrift</strong></td>
            <td>{ruleset.soll_credit_status_codes.join(", ")}</td>
          </tr>
          <tr>
            <td><strong>Wochen-Definition</strong></td>
            <td>{ruleset.week_boundary === "monday_to_sunday" ? "Montag – Sonntag" : "ISO-Woche"}</td>
          </tr>
          <tr>
            <td><strong>Schwellen-Modell für Teilzeit</strong></td>
            <td>
              {ruleset.threshold_model === "same_for_all" && "Gleicher Standard für Voll- und Teilzeit; individuelle Mitarbeitergrenzen haben Vorrang"}
              {ruleset.threshold_model === "per_soll" && "Mehrstunden = Soll bis Soll+3,5h (immer 3,5h breit)"}
              {ruleset.threshold_model === "fulltime_soll" && "Mehrstunden enden bei Vollzeit-Soll 38,5h"}
            </td>
          </tr>
          <tr>
            <td><strong>K/U/F zählen ins Wochen-Total</strong></td>
            <td>{ruleset.status_days_count_to_weekly ? "Ja (Soll-Stunden werden mitgezählt)" : "Nein"}</td>
          </tr>
          <tr>
            <td><strong>100%-Zuschlag</strong></td>
            <td>{ruleset.ueberstunden_100_enabled ? "Aktiv (Sonntag/Feiertag)" : "Nicht aktiv"}</td>
          </tr>
          <tr>
            <td><strong>Mehrstunden-Routing</strong></td>
            <td>
              {ruleset.mehrstunden_routing === "always_za" && "Immer aufs ZA-Konto (nie ausbezahlt)"}
              {ruleset.mehrstunden_routing === "always_cash" && "Immer ausbezahlt"}
              {ruleset.mehrstunden_routing === "per_employee" && "Pro Mitarbeiter konfigurierbar"}
            </td>
          </tr>
          <tr>
            <td><strong>Default Pay-Mode (neue Mitarbeiter)</strong></td>
            <td>
              {ruleset.default_ueberstunden_pay_mode === "za" ? "ZA-Konto" : "Auszahlung (cash)"}
            </td>
          </tr>
        </tbody>
      </table>
      </>)}

      {(ruleset as any).uebersetzungsmatrix && (
        <UebersetzungsmatrixBlock matrix={(ruleset as any).uebersetzungsmatrix} />
      )}

      <h4 style={{ marginBottom: 8 }}>Regelwerk im Detail</h4>
      <ol style={{ paddingLeft: 24 }}>
        {ruleset.rules.map((rule, i) => (
          <li key={i} style={{ marginBottom: 14 }}>
            <strong>{rule.label}</strong>
            <div style={{ fontSize: 13, color: "#374151" }}>{rule.description}</div>
            {rule.implementation && (
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, fontFamily: "monospace" }}>
                Implementation: {rule.implementation}
              </div>
            )}
          </li>
        ))}
      </ol>

      {!(ruleset as any).calc_model?.startsWith("zulagen") && (<>
      <h4 style={{ marginBottom: 8 }}>RZL-Export-Labels pro Pay-Mode</h4>
      <table className="data-table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>Pay-Mode</th>
            <th>Mehrstunden → RZL</th>
            <th>Überstunden 50% → RZL</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>cash</strong> (Auszahlung)</td>
            <td>{ruleset.rzl_export_labels.cash.mehrstunden}</td>
            <td>{ruleset.rzl_export_labels.cash.ueberstunden_50}</td>
          </tr>
          <tr>
            <td><strong>za</strong> (ZA-Konto)</td>
            <td>{ruleset.rzl_export_labels.za.mehrstunden}</td>
            <td>{ruleset.rzl_export_labels.za.ueberstunden_50}</td>
          </tr>
          <tr>
            <td><strong>pauschale</strong> (Vertrags-Pauschale)</td>
            <td>{ruleset.rzl_export_labels.pauschale.mehrstunden}</td>
            <td>{ruleset.rzl_export_labels.pauschale.ueberstunden_50}</td>
          </tr>
        </tbody>
      </table>

      <div className="help-block" style={{ marginTop: 24 }}>
        <strong>Beispiele aus der Owner-Klärung (2026-05-18):</strong>
        <ul style={{ marginTop: 8 }}>
          <li>
            MA <strong>x</strong> = Mehrstunden→ZA, Überstunden→Cash<br/>
            RZL-Label: <em>„Überstunden"</em> (= Auszahlungs-Zeile)
          </li>
          <li>
            MA <strong>y</strong> = Mehrstunden→ZA, Überstunden→ZA<br/>
            RZL-Label: <em>„ZA Kartei importer"</em> (= ZA-Konto-Sammlung)
          </li>
          <li>
            MA <strong>z</strong> = Überstunden-<strong>Pauschale</strong> (Vertrag)<br/>
            Berechnung: Soll-Stunden = Maximum, KEINE Mehrstunden/Überstunden anerkannt.<br/>
            CSV-Spalten Std + ZA = 0. Status-Tage (K/U/F) zählen normal.<br/>
            RZL-Label: <em>„Pauschal-abgedeckt"</em>
          </li>
        </ul>
      </div>
      </>)}
    </div>
  );
}

/**
 * T15: Übersetzungsmatrix-Renderer — zeigt das Daten-Regelwerk PDF↔Excel↔RZL
 * aus client_rules.ruleset.uebersetzungsmatrix. ANNAHMEN (vorläufige
 * Zuordnungen, warten auf Kunden-Antwort) sind sichtbar gelb markiert —
 * wir bauen auf Stein: jede Annahme steht DA, wo sie wirkt.
 */
function UebersetzungsmatrixBlock({ matrix }: { matrix: any }) {
  const rows: any[] = Array.isArray(matrix?.zulagen) ? matrix.zulagen : [];
  const isAnnahme = (s: string | null | undefined) => !!s && s.toUpperCase().startsWith("ANNAHME");
  return (
    <div style={{ marginBottom: 24 }}>
      <h4 style={{ marginBottom: 8 }}>Übersetzungsmatrix (PDF ↔ Excel ↔ RZL-Lohnart)</h4>
      {matrix._status && (
        <div style={{
          padding: 10, marginBottom: 10, fontSize: 12, borderRadius: 6,
          background: "#fef9c3", border: "1px solid #fcd34d", color: "#713f12",
        }}>
          {matrix._status}
        </div>
      )}
      <table className="data-table" style={{ fontSize: 13 }}>
        <thead>
          <tr><th>PDF-Formular</th><th>Excel-Export</th><th>RZL-Lohnart</th><th>Status</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={isAnnahme(r.status) ? { background: "#fefce8" } : undefined}>
              <td>{r.pdf ?? "—"}</td>
              <td>{r.excel ?? "—"}</td>
              <td>{r.lohnart ?? "—"}</td>
              <td style={isAnnahme(r.status) ? { color: "#a16207", fontWeight: 600 } : { color: "#374151" }}>
                {r.status}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {matrix.kostenstellen_splits && (
        <div style={{ fontSize: 12, color: "#a16207", marginTop: 6 }}>
          Kostenstellen-Splits: {matrix.kostenstellen_splits}
        </div>
      )}
    </div>
  );
}
